import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Mail, 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Calendar,
  Store,
  TrendingUp,
  Eye,
  History,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeeklyReports() {
  useAuth();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [statusRes, historyRes, restaurantsRes] = await Promise.all([
        axios.get(`${API}/admin/weekly-reports/status`),
        axios.get(`${API}/admin/weekly-reports/history?limit=5`),
        axios.get(`${API}/admin/restaurants`)
      ]);
      
      setStatus(statusRes.data);
      setHistory(historyRes.data.history || []);
      setRestaurants(restaurantsRes.data.filter(r => r.status === 'active'));
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePreview = async (restaurantId) => {
    try {
      const response = await axios.get(`${API}/admin/weekly-reports/preview/${restaurantId}`);
      setPreviewData(response.data);
    } catch (err) {
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  const handleSendSingle = async (restaurantId) => {
    try {
      setSendingId(restaurantId);
      const response = await axios.post(`${API}/admin/weekly-reports/send/${restaurantId}`, {});
      
      if (response.data.skipped) {
        toast.info(`Rapport non envoyé : ${response.data.reason || 'Aucune commande cette semaine'}`);
      } else {
        toast.success(`Rapport envoyé à ${response.data.restaurant_name}`);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors de l'envoi"));
    } finally {
      setSendingId(null);
    }
  };

  const handleSendAll = async () => {
    if (!confirm('Êtes-vous sûr de vouloir envoyer les rapports à TOUS les restaurants actifs ?')) {
      return;
    }
    
    try {
      setSending(true);
      const response = await axios.post(`${API}/admin/weekly-reports/send-all`, {});
      
      toast.success(
        `Rapports envoyés ! ${response.data.sent} envoyés, ${response.data.skipped} ignorés, ${response.data.failed} échoués`
      );
      
      fetchData(); // Refresh history
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors de l'envoi"));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Rapports Hebdomadaires">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Rapports Hebdomadaires">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Rapports Hebdomadaires</h2>
            <p className="text-sm text-gray-500">
              Envoyez les statistiques hebdomadaires aux restaurants
            </p>
          </div>
          <Button 
            onClick={handleSendAll}
            disabled={sending}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Envoyer à tous
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Store className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Restaurants actifs</p>
                  <p className="text-2xl font-bold">{status?.active_restaurants || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Prochain envoi prévu</p>
                  <p className="text-lg font-semibold">{formatDate(status?.next_scheduled)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <History className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Dernier envoi</p>
                  <p className="text-lg font-semibold">
                    {status?.last_report ? formatDate(status.last_report.timestamp) : 'Jamais'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* History */}
        {history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="w-5 h-5 text-gray-500" />
                Historique des envois
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((report, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm">{formatDate(report.timestamp)}</p>
                        <p className="text-xs text-gray-500">
                          {report.total} restaurants ciblés
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {report.sent} envoyés
                      </Badge>
                      {report.skipped > 0 && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {report.skipped} ignorés
                        </Badge>
                      )}
                      {report.failed > 0 && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="w-3 h-3 mr-1" />
                          {report.failed} échoués
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Restaurant List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="w-5 h-5 text-gray-500" />
              Restaurants actifs
            </CardTitle>
            <CardDescription>
              Prévisualisez ou envoyez un rapport individuel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {restaurants.map((restaurant) => (
                <div 
                  key={restaurant.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="font-medium">{restaurant.name}</p>
                    <p className="text-sm text-gray-500">{restaurant.city}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreview(restaurant.id)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Aperçu
                    </Button>
                    <Button
                      size="sm"
                      disabled={sendingId === restaurant.id}
                      onClick={() => handleSendSingle(restaurant.id)}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      {sendingId === restaurant.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
              
              {restaurants.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  Aucun restaurant actif
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview Modal */}
        {previewData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Aperçu : {previewData.restaurant.name}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setPreviewData(null)}>
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="text-sm text-gray-500">
                  <p><strong>Email destinataire :</strong> {previewData.restaurant.owner_email || 'Non défini'}</p>
                  <p><strong>Période :</strong> {previewData.stats.period?.start} → {previewData.stats.period?.end}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">{previewData.stats.total_orders}</p>
                    <p className="text-xs text-gray-600">Commandes</p>
                    <p className={`text-xs ${previewData.stats.orders_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {previewData.stats.orders_change >= 0 ? '+' : ''}{previewData.stats.orders_change}%
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{previewData.stats.total_revenue} €</p>
                    <p className="text-xs text-gray-600">Chiffre d'affaires</p>
                    <p className={`text-xs ${previewData.stats.revenue_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {previewData.stats.revenue_change >= 0 ? '+' : ''}{previewData.stats.revenue_change}%
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{previewData.stats.avg_rating || 0} ⭐</p>
                    <p className="text-xs text-gray-600">Note moyenne</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{previewData.stats.new_reviews || 0}</p>
                    <p className="text-xs text-gray-600">Nouveaux avis</p>
                  </div>
                </div>
                
                {previewData.stats.top_dishes?.length > 0 && (
                  <div>
                    <p className="font-medium text-sm mb-2">Top plats vendus</p>
                    <div className="space-y-1">
                      {previewData.stats.top_dishes.map((dish, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{['🥇', '🥈', '🥉', '4.', '5.'][i]} {dish.name}</span>
                          <span className="text-gray-500">{dish.count} vendus</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className={`p-3 rounded-lg text-center ${previewData.would_send ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                  <p className={`text-sm font-medium ${previewData.would_send ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {previewData.would_send 
                      ? '✅ Ce rapport sera envoyé'
                      : '⚠️ Ce rapport ne sera pas envoyé (0 commande)'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
